import type { RefObject } from 'react';
import React, { useRef, useCallback, useState, useMemo } from 'react';
import { SectionRefsContext } from './SectionRefsContext';

type SectionRefsProviderProps = {
  children: React.ReactNode;
};

export const SectionRefsProvider = ({ children }: SectionRefsProviderProps) => {
  const sectionRefsMap = useRef(new Map<string, RefObject<HTMLDivElement>>());
  const [refsVersion, setRefsVersion] = useState(0);

  const registerSectionRef = useCallback(
    (sectionId: string, ref: RefObject<HTMLDivElement>) => {
      const existingRef = sectionRefsMap.current.get(sectionId);
      if (existingRef !== ref) {
        sectionRefsMap.current.set(sectionId, ref);
        setRefsVersion((prev) => prev + 1);
      }
    },
    [setRefsVersion]
  );

  const getSectionRef = useCallback((sectionId: string) => {
    return sectionRefsMap.current.get(sectionId);
  }, []);

  const sectionRefs = useMemo(() => {
    return Array.from(sectionRefsMap.current.values());
  }, [refsVersion]);

  const contextValue = useMemo(
    () => ({
      registerSectionRef,
      getSectionRef,
      sectionRefs,
    }),
    [registerSectionRef, getSectionRef, sectionRefs]
  );

  return <SectionRefsContext.Provider value={contextValue}>{children}</SectionRefsContext.Provider>;
};
